export const transformPayload = (payload: Record<string, any>, propsToChange: Record<string, string>) => {
    for(const propName in propsToChange) {
      if(payload.hasOwnProperty(propName)) {
        const newPropName = propsToChange[propName];
        
        payload[newPropName] = payload[propName];
        delete payload[propName];
      }
    }
  };


type Plan = {
  name: string;
  codname: string;
  features: Record<string, boolean>,
};

type CustomerType = "consultant" | "company";

type Tier = {
  flat_amount?: number;
  start_from: number;
  unit_amount?: number;
  up_to: number;
};

type Package = {
  amount: number;
  is_per_seat?: boolean;
  tiers?: Tier[];
  meta?: {
    regular_price?: string | number;
  };
};

const VAT_RATE = 1.22;
const CONSULTANT_DISCOUNT = 0.3;

export const roundMoney = (value: number) =>
  Number(Number(value || 0).toFixed(2));

export const removeVat = (value: number | string) =>
  Number(value || 0) / VAT_RATE;

export const hasPackagePromo = (pkg?: Package) =>
  Boolean(pkg?.meta?.regular_price);

export const calculatePackageCost = (
  pkg: Package,
  employeeCount: number,
  customerType: CustomerType
) => {
  const qty = Number(employeeCount || 1);

  const amountCost = pkg.is_per_seat
    ? pkg.tiers?.length
      ? calculateTotalCost(qty, pkg.tiers, customerType).original
      : removeVat(pkg.amount) * qty
    : removeVat(pkg.amount);

  const regularRaw = pkg.meta?.regular_price;

  const regularCost = regularRaw
    ? pkg.is_per_seat
      ? removeVat(regularRaw) * qty
      : removeVat(regularRaw)
    : null;

  return {
    amountCost: roundMoney(amountCost),
    regularCost: regularCost !== null ? roundMoney(regularCost) : null,
    hasPromo: regularCost !== null,
  };
};

export const calculateTotalCost = (
  employeeCount: number,
  tiers: Tier[],
  customerType: CustomerType,
  addonCost = 0,
  addonRegularCost?: number
) => {
  const tiersFiltered: Tier[] = [];

  for (const tier of tiers) {
    tiersFiltered.push(tier);
    if (tier.up_to >= employeeCount) break;
  }

  const basePrice = tiersFiltered.reduce((acc, curr) => {
    const unitAmount = removeVat(curr.unit_amount || 0);
    const flatAmount = removeVat(curr.flat_amount || 0);
    const upTo = Math.min(curr.up_to, employeeCount);
    const startFrom = curr.start_from;

    const employeesToCalc = upTo + 1 - startFrom;

    return acc + unitAmount * employeesToCalc + flatAmount;
  }, 0);

  const originalAddonCost = addonRegularCost ?? addonCost;

  const originalTotal = basePrice + originalAddonCost;
  let discountedTotal = basePrice + addonCost;

  if (customerType === "consultant") {
    discountedTotal *= 1 - CONSULTANT_DISCOUNT;
  }

  return {
    original: roundMoney(originalTotal),
    discounted: roundMoney(discountedTotal),
    hasPromo: originalTotal > discountedTotal,
  };
};


export const estraiMessaggiErrore = (error: any): string => {
  let errorMessage = 'Errore, riprova più tardi.';

  if (error?.response?.data) {
    const errorData = error.response.data;
    const errorMessages: string[] = [];

    Object.keys(errorData).forEach((key) => {
      const messageArray = errorData[key];
      if (Array.isArray(messageArray)) {
        errorMessages.push(...messageArray);
      } else if (typeof messageArray === 'string') {
        errorMessages.push(messageArray);
      }
    });

    if (errorMessages.length > 0) {
      errorMessage = errorMessages.join(' ');
    }
  }

  return errorMessage;
};


export const v2EstraiMessaggiErrore = (error: any): string => {
  const fallback = 'Errore, riprova più tardi.';
  const data = error?.response?.data;
  if (!data) return fallback;

  const out: string[] = [];

  const visit = (value: any): void => {
    if (value == null) return;
    if (typeof value === 'string') {
      const v = value.trim();
      if (v) out.push(v);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value === 'object') {
      Object.values(value).forEach(visit);
    }
  };

  if (typeof data?.detail === 'string') out.push(data.detail);
  if (Array.isArray(data?.non_field_errors)) data.non_field_errors.forEach(visit);

  visit(data);

  const unique = [...new Set(out)];
  return unique.length ? unique.join(' ') : fallback;
};


const DEFAULT_MSG = "Errore, riprova più tardi.";

export function getUiErrorMessage(error: any): string {
  const fromApi = estraiMessaggiErrore(error);
  if (fromApi && fromApi !== DEFAULT_MSG) return fromApi;

  const local = error?.message;
  if (typeof local === "string" && local.trim()) return local.trim();

  return DEFAULT_MSG;
}